import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { API_URL } from '../config/api';

const AdminComprehensiveReport = () => {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#667eea';
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sports, setSports] = useState([]);
  const [courts, setCourts] = useState([]);
  const [activeView, setActiveView] = useState('detailed'); // detailed, by-sport, by-court, trends, peak-hours, customers

  // Filters
  const [filters, setFilters] = useState({
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().split('T')[0];
    })(),
    endDate: new Date().toISOString().split('T')[0],
    sportId: 'all',
    courtId: 'all',
    platform: 'all',
    status: 'all',
    source: 'all',
    groupBy: 'day',
    bulkOnly: false
  });

  // Aggregated reports data
  const [aggregatedData, setAggregatedData] = useState({
    bySport: [],
    byCourt: [],
    trends: [],
    peakHours: [],
    customers: []
  });

  useEffect(() => {
    fetchSports();
    fetchCourts();
  }, []);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchAllReports();
    }
  }, [filters]);

  const fetchSports = async () => {
    try {
      const response = await fetch(`${API_URL}/sports`);
      if (response.ok) {
        const data = await response.json();
        setSports(data);
      }
    } catch (error) {
      console.error('Error fetching sports:', error);
    }
  };

  const fetchCourts = async () => {
    try {
      const response = await fetch(`${API_URL}/courts`);
      if (response.ok) {
        const data = await response.json();
        setCourts(data.courts || data);
      }
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.sportId !== 'all') params.append('sport_id', filters.sportId);
      if (filters.courtId !== 'all') params.append('court_id', filters.courtId);
      if (filters.platform !== 'all') params.append('platform', filters.platform);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.source !== 'all') params.append('source', filters.source);
      if (filters.bulkOnly) params.append('bulk_only', 'true');

      // Fetch detailed report (main data source)
      const detailedResponse = await fetch(`${API_URL}/reports/bookings/detailed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (detailedResponse.ok) {
        const detailedData = await detailedResponse.json();
        setReportData(detailedData.data || []);
        setSummary(detailedData.summary || null);

        // Calculate aggregated views from detailed data
        calculateAggregatedData(detailedData.data || []);
      } else {
        setError('Failed to fetch report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      setError('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const calculateAggregatedData = (data) => {
    // Filter confirmed bookings for revenue calculations
    const confirmedBookings = data.filter(b => b.status === 'confirmed');

    // By Sport
    const sportMap = {};
    confirmedBookings.forEach(b => {
      const key = b.sportName;
      if (!sportMap[key]) {
        sportMap[key] = { sport_name: key, booking_count: 0, total_revenue: 0, total_commission: 0 };
      }
      sportMap[key].booking_count++;
      sportMap[key].total_revenue += parseFloat(b.finalAmount || 0);
      sportMap[key].total_commission += parseFloat(b.commissionAmount || 0);
    });
    const bySport = Object.values(sportMap).map(s => ({
      ...s,
      net_revenue: s.total_revenue - s.total_commission
    })).sort((a, b) => b.total_revenue - a.total_revenue);

    // By Court
    const courtMap = {};
    confirmedBookings.forEach(b => {
      const key = b.courtName;
      if (!courtMap[key]) {
        courtMap[key] = { court_name: key, sport_name: b.sportName, booking_count: 0, total_revenue: 0, total_commission: 0 };
      }
      courtMap[key].booking_count++;
      courtMap[key].total_revenue += parseFloat(b.finalAmount || 0);
      courtMap[key].total_commission += parseFloat(b.commissionAmount || 0);
    });
    const byCourt = Object.values(courtMap).map(c => ({
      ...c,
      net_revenue: c.total_revenue - c.total_commission
    })).sort((a, b) => b.total_revenue - a.total_revenue);

    // Trends (by day/week/month)
    const trendMap = {};
    confirmedBookings.forEach(b => {
      let period = b.bookingDate;
      if (filters.groupBy === 'week') {
        const date = new Date(b.bookingDate);
        const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
        period = weekStart.toISOString().split('T')[0];
      } else if (filters.groupBy === 'month') {
        period = b.bookingDate?.substring(0, 7);
      }
      if (!trendMap[period]) {
        trendMap[period] = { period, booking_count: 0, total_revenue: 0 };
      }
      trendMap[period].booking_count++;
      trendMap[period].total_revenue += parseFloat(b.finalAmount || 0);
    });
    const trends = Object.values(trendMap)
      .map(t => ({ ...t, avg_revenue: t.booking_count > 0 ? t.total_revenue / t.booking_count : 0 }))
      .sort((a, b) => b.period?.localeCompare(a.period));

    // Peak Hours
    const hourMap = {};
    confirmedBookings.forEach(b => {
      if (b.startTime) {
        const hour = parseInt(b.startTime.split(':')[0]);
        const hourSlot = `${hour}:00 - ${hour + 1}:00`;
        if (!hourMap[hour]) {
          hourMap[hour] = { hour, hour_slot: hourSlot, booking_count: 0, total_revenue: 0 };
        }
        hourMap[hour].booking_count++;
        hourMap[hour].total_revenue += parseFloat(b.finalAmount || 0);
      }
    });
    const peakHours = Object.values(hourMap).sort((a, b) => a.hour - b.hour);

    // Customers
    const customerMap = {};
    confirmedBookings.forEach(b => {
      const key = b.customerPhone || b.customerEmail || b.customerName;
      if (!customerMap[key]) {
        customerMap[key] = {
          customer_name: b.customerName,
          customer_email: b.customerEmail,
          customer_phone: b.customerPhone,
          total_bookings: 0,
          total_spent: 0,
          last_booking_date: b.bookingDate
        };
      }
      customerMap[key].total_bookings++;
      customerMap[key].total_spent += parseFloat(b.finalAmount || 0);
      if (b.bookingDate > customerMap[key].last_booking_date) {
        customerMap[key].last_booking_date = b.bookingDate;
      }
    });
    const customers = Object.values(customerMap).sort((a, b) => b.total_spent - a.total_spent);

    setAggregatedData({ bySport, byCourt, trends, peakHours, customers });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) return;

    const headers = [
      'Bill Number', 'Booking ID', 'Booking Ref', 'Date', 'Time Slot', 'Customer Name', 'Phone',
      'Sport', 'Court', 'Booking Type', 'Platform/Partner', 'Booked By',
      'Base Amount', 'Discount', 'Discount Applied By', 'Final Amount', 'Commission', 'Net Payable',
      'Payment Mode', 'Payment Status', 'Transaction ID', 'Status', 'Notes'
    ];

    const rows = reportData.map(row => [
      row.billNumber || 'N/A',
      row.bookingId,
      row.bookingReference,
      formatDate(row.bookingDate),
      `${formatTime(row.startTime)} - ${formatTime(row.endTime)}`,
      row.customerName,
      row.customerPhone,
      row.sportName,
      row.courtName,
      row.bookingType,
      row.partnerName || 'N/A',
      row.bookedByStaff || 'N/A',
      row.baseAmount,
      row.discount,
      row.discountAppliedBy || 'N/A',
      row.finalAmount,
      row.commissionAmount,
      row.netPayable,
      row.paymentMode,
      row.paymentStatus,
      row.transactionId || 'N/A',
      row.status,
      (row.notes || '').replace(/,/g, ';')
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booking_report_${filters.startDate}_to_${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: { bg: '#dcfce7', color: '#166534' },
      cancelled: { bg: '#fee2e2', color: '#991b1b' },
      pending: { bg: '#fef3c7', color: '#92400e' },
      blocked: { bg: '#e5e7eb', color: '#374151' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {status}
      </span>
    );
  };

  const viewTabs = [
    { id: 'detailed', label: 'All Bookings' },
    { id: 'by-sport', label: 'By Sport' },
    { id: 'by-court', label: 'By Court' },
    { id: 'trends', label: 'Trends' },
    { id: 'peak-hours', label: 'Peak Hours' },
    { id: 'customers', label: 'Customers' }
  ];

  const renderDetailedView = () => (
    <div style={{ overflowX: 'auto' }}>
      <table className="admin-table" style={{ minWidth: '1800px' }}>
        <thead>
          <tr>
            <th style={{ minWidth: '70px' }}>Bill #</th>
            <th style={{ minWidth: '100px' }}>Booking Ref</th>
            <th style={{ minWidth: '90px' }}>Date</th>
            <th style={{ minWidth: '100px' }}>Time</th>
            <th style={{ minWidth: '120px' }}>Customer</th>
            <th style={{ minWidth: '100px' }}>Phone</th>
            <th style={{ minWidth: '80px' }}>Sport</th>
            <th style={{ minWidth: '120px' }}>Court</th>
            <th style={{ minWidth: '80px' }}>Type</th>
            <th style={{ minWidth: '80px' }}>Platform</th>
            <th style={{ minWidth: '80px' }}>Base</th>
            <th style={{ minWidth: '70px' }}>Discount</th>
            <th style={{ minWidth: '90px' }}>Discount By</th>
            <th style={{ minWidth: '80px' }}>Final</th>
            <th style={{ minWidth: '80px' }}>Commission</th>
            <th style={{ minWidth: '80px' }}>Net</th>
            <th style={{ minWidth: '70px' }}>Payment</th>
            <th style={{ minWidth: '70px' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {reportData.map((row, idx) => {
            // Check if this is the first booking of a bulk group for visual grouping
            const isBulk = row.billNumber && row.billNumber.includes('-');
            const bulkGroupId = isBulk ? row.billNumber.split('-')[0] : null;
            const prevBulkGroupId = idx > 0 && reportData[idx-1].billNumber ? reportData[idx-1].billNumber.split('-')[0] : null;
            const isNewBulkGroup = isBulk && bulkGroupId !== prevBulkGroupId;

            return (
            <tr key={row.bookingId} style={{
              ...(row.status === 'cancelled' ? { backgroundColor: '#fef2f2' } : parseFloat(row.discount) > 0 ? { backgroundColor: '#fef3c7' } : isBulk ? { backgroundColor: '#f0fdf4' } : {}),
              ...(isNewBulkGroup ? { borderTop: '2px solid #10b981' } : {})
            }}>
              <td style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: isBulk ? 600 : 400, color: isBulk ? '#059669' : '#6b7280' }}>
                {row.billNumber || '-'}
              </td>
              <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{row.bookingReference}</td>
              <td style={{ fontSize: '12px' }}>{formatDate(row.bookingDate)}</td>
              <td style={{ fontSize: '11px' }}>{formatTime(row.startTime)} - {formatTime(row.endTime)}</td>
              <td>
                <div style={{ fontWeight: 600, fontSize: '12px' }}>{row.customerName}</div>
              </td>
              <td style={{ fontSize: '11px' }}>{row.customerPhone}</td>
              <td style={{ fontSize: '12px' }}>{row.sportName}</td>
              <td style={{ fontSize: '12px' }}>{row.courtName}</td>
              <td>
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  backgroundColor: row.bookingType === 'Direct' ? '#dbeafe' : '#fef3c7',
                  color: row.bookingType === 'Direct' ? '#1e40af' : '#92400e'
                }}>
                  {row.bookingType}
                </span>
              </td>
              <td style={{ fontSize: '11px' }}>{row.platform}</td>
              <td style={{ fontWeight: 500 }}>{formatCurrency(row.baseAmount)}</td>
              <td style={{ color: parseFloat(row.discount) > 0 ? '#f59e0b' : '#9ca3af' }}>
                {parseFloat(row.discount) > 0 ? `-${formatCurrency(row.discount)}` : '-'}
              </td>
              <td style={{ fontSize: '11px', color: row.discountAppliedBy ? '#7c3aed' : '#9ca3af', fontWeight: row.discountAppliedBy ? 500 : 400 }}>
                {row.discountAppliedBy || '-'}
              </td>
              <td style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(row.finalAmount)}</td>
              <td style={{ color: parseFloat(row.commissionAmount) > 0 ? '#ef4444' : '#9ca3af' }}>
                {parseFloat(row.commissionAmount) > 0 ? formatCurrency(row.commissionAmount) : '-'}
              </td>
              <td style={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(row.netPayable)}</td>
              <td style={{ fontSize: '11px', textTransform: 'uppercase' }}>{row.paymentMode}</td>
              <td>{getStatusBadge(row.status)}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderBySportView = () => (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Sport</th>
            <th style={{ textAlign: 'right' }}>Bookings</th>
            <th style={{ textAlign: 'right' }}>Revenue</th>
            <th style={{ textAlign: 'right' }}>Commission</th>
            <th style={{ textAlign: 'right' }}>Net Revenue</th>
          </tr>
        </thead>
        <tbody>
          {aggregatedData.bySport.map((row, idx) => (
            <tr key={idx}>
              <td style={{ fontWeight: 600 }}>{row.sport_name}</td>
              <td style={{ textAlign: 'right' }}>{row.booking_count}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total_revenue)}</td>
              <td style={{ textAlign: 'right', color: '#f59e0b' }}>{formatCurrency(row.total_commission)}</td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(row.net_revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderByCourtView = () => (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Court</th>
            <th>Sport</th>
            <th style={{ textAlign: 'right' }}>Bookings</th>
            <th style={{ textAlign: 'right' }}>Revenue</th>
            <th style={{ textAlign: 'right' }}>Commission</th>
            <th style={{ textAlign: 'right' }}>Net Revenue</th>
          </tr>
        </thead>
        <tbody>
          {aggregatedData.byCourt.map((row, idx) => (
            <tr key={idx}>
              <td style={{ fontWeight: 600 }}>{row.court_name}</td>
              <td>{row.sport_name}</td>
              <td style={{ textAlign: 'right' }}>{row.booking_count}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total_revenue)}</td>
              <td style={{ textAlign: 'right', color: '#f59e0b' }}>{formatCurrency(row.total_commission)}</td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(row.net_revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderTrendsView = () => (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['day', 'week', 'month'].map(g => (
          <button
            key={g}
            onClick={() => setFilters({ ...filters, groupBy: g })}
            className={`admin-btn ${filters.groupBy === g ? 'admin-btn-primary' : 'admin-btn-secondary'} admin-btn-sm`}
            style={filters.groupBy === g ? { backgroundColor: primaryColor } : {}}
          >
            {g.charAt(0).toUpperCase() + g.slice(1)}ly
          </button>
        ))}
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Period</th>
              <th style={{ textAlign: 'right' }}>Bookings</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>Avg/Booking</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedData.trends.map((row, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: 600 }}>{row.period}</td>
                <td style={{ textAlign: 'right' }}>{row.booking_count}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total_revenue)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(row.avg_revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPeakHoursView = () => {
    const maxBookings = Math.max(...aggregatedData.peakHours.map(r => r.booking_count || 0), 1);
    return (
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Time Slot</th>
              <th style={{ textAlign: 'right' }}>Bookings</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ width: '40%' }}>Activity Level</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedData.peakHours.map((row, idx) => {
              const percentage = (row.booking_count / maxBookings) * 100;
              return (
                <tr key={idx}>
                  <td style={{ fontWeight: 600 }}>{row.hour_slot}</td>
                  <td style={{ textAlign: 'right' }}>{row.booking_count}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total_revenue)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, background: '#e5e7eb', borderRadius: '4px', height: '20px' }}>
                        <div style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}cc)`,
                          height: '100%',
                          borderRadius: '4px',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '36px' }}>{Math.round(percentage)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCustomersView = () => (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Email</th>
            <th>Phone</th>
            <th style={{ textAlign: 'right' }}>Bookings</th>
            <th style={{ textAlign: 'right' }}>Total Spent</th>
            <th>Last Booking</th>
          </tr>
        </thead>
        <tbody>
          {aggregatedData.customers.map((row, idx) => (
            <tr key={idx}>
              <td style={{ fontWeight: 600 }}>{row.customer_name}</td>
              <td>{row.customer_email}</td>
              <td>{row.customer_phone}</td>
              <td style={{ textAlign: 'right' }}>{row.total_bookings}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total_spent)}</td>
              <td>{formatDate(row.last_booking_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCurrentView = () => {
    if (loading) {
      return (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading report data...</p>
        </div>
      );
    }

    if (reportData.length === 0) {
      return (
        <div className="admin-empty">
          <div className="admin-empty-icon">📊</div>
          <p>No data available for the selected filters</p>
        </div>
      );
    }

    switch (activeView) {
      case 'detailed': return renderDetailedView();
      case 'by-sport': return renderBySportView();
      case 'by-court': return renderByCourtView();
      case 'trends': return renderTrendsView();
      case 'peak-hours': return renderPeakHoursView();
      case 'customers': return renderCustomersView();
      default: return renderDetailedView();
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Reports</h1>
        <p>Comprehensive booking and revenue reports</p>
      </div>

      {error && (
        <div className="admin-alert admin-alert-danger">{error}</div>
      )}

      {/* View Tabs */}
      <div className="admin-card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '4px', padding: '12px', flexWrap: 'wrap' }}>
          {viewTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`admin-btn ${activeView === tab.id ? 'admin-btn-primary' : 'admin-btn-secondary'} admin-btn-sm`}
              style={activeView === tab.id ? { backgroundColor: primaryColor } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: '16px' }}>
        <div className="admin-card-header">Filters</div>
        <div style={{ padding: '16px' }}>
          {/* Row 1: Dates and dropdowns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label className="admin-label">Start Date</label>
              <input
                type="date"
                className="admin-input"
                style={{ width: '100%' }}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div>
              <label className="admin-label">End Date</label>
              <input
                type="date"
                className="admin-input"
                style={{ width: '100%' }}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            <div>
              <label className="admin-label">Sport</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={filters.sportId}
                onChange={(e) => setFilters({ ...filters, sportId: e.target.value })}
              >
                <option value="all">All Sports</option>
                {sports.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="admin-label">Court</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={filters.courtId}
                onChange={(e) => setFilters({ ...filters, courtId: e.target.value })}
              >
                <option value="all">All Courts</option>
                {courts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="admin-label">Platform</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={filters.platform}
                onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
              >
                <option value="all">All</option>
                <option value="direct">Direct</option>
                <option value="playo">Playo</option>
                <option value="hudle">Hudle</option>
              </select>
            </div>
            <div>
              <label className="admin-label">Status</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">All</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="admin-label">Source</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              >
                <option value="all">All</option>
                <option value="frontdesk">Frontdesk</option>
                <option value="online">Online</option>
                <option value="partner_api">Partner API</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: filters.bulkOnly ? primaryColor : '#f3f4f6',
                  color: filters.bulkOnly ? 'white' : '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  height: '38px',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={filters.bulkOnly}
                  onChange={(e) => setFilters({ ...filters, bulkOnly: e.target.checked })}
                  style={{ margin: 0 }}
                />
                Bulk Only
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={exportToCSV}
                className="admin-btn"
                style={{
                  width: '100%',
                  height: '38px',
                  backgroundColor: reportData.length === 0 ? '#d1d5db' : '#10b981',
                  color: 'white',
                  border: 'none',
                  fontWeight: 600
                }}
                disabled={reportData.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div className="admin-card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: primaryColor }}>{summary.totalBookings}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Bookings</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
              <span style={{ color: '#10b981' }}>{summary.confirmedBookings}</span> |
              <span style={{ color: '#ef4444' }}> {summary.cancelledBookings}</span> |
              <span style={{ color: '#f59e0b' }}> {summary.pendingBookings}</span>
            </div>
          </div>
          <div className="admin-card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>{formatCurrency(summary.totalAmount)}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Amount</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
              Base: {formatCurrency(summary.totalBaseAmount)}
            </div>
          </div>
          <div className="admin-card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(summary.totalDiscounts)}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Discounts</div>
          </div>
          <div className="admin-card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(summary.totalCommission)}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Commission</div>
          </div>
          <div className="admin-card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>{formatCurrency(summary.netRevenue)}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Net Revenue</div>
          </div>
        </div>
      )}

      {/* Payment & Source Breakdown */}
      {summary && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-header">Payment & Source Breakdown</div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Cash</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatCurrency(summary.cashPayments)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>UPI</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatCurrency(summary.upiPayments)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Card</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatCurrency(summary.cardPayments)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Online</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatCurrency(summary.onlinePayments)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Direct Bookings</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{summary.directBookings}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Partner Bookings</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{summary.partnerBookings}</div>
            </div>
          </div>
        </div>
      )}

      {/* Report Content */}
      <div className="admin-card">
        <div className="admin-card-header">
          {viewTabs.find(t => t.id === activeView)?.label} {reportData.length > 0 && `(${reportData.length})`}
        </div>
        <div className="admin-card-body">
          {renderCurrentView()}
        </div>
      </div>
    </div>
  );
};

export default AdminComprehensiveReport;
